import { createMemo, createSignal } from 'solid-js'
import {
   getMetricDefinition,
   mergeMeasurementEntries,
   validateMeasurementEntry,
   type MeasurementEntry,
   type MetricId,
} from '@fasting-timer/shared'

import { translate } from '../i18n'
import { dispatchMeasurementsStateChange } from '../runtimeBridge'
import type { MeasurementUpsertOutcome } from '../account/measurementsApi'

const MEASUREMENTS_STORAGE_KEY = 'measurements'
const PENDING_STORAGE_KEY = 'measurementsPending'

type MeasurementStatus = {
   message: string
   isError: boolean
}

/**
 * Reads and JSON-parses a localStorage key, returning null on any failure.
 *
 * @param key - The storage key.
 * @returns The parsed value, or null.
 */
function readJson(key: string): unknown {
   try {
      const raw = localStorage.getItem(key)
      return raw ? JSON.parse(raw) : null
   } catch {
      return null
   }
}

/**
 * JSON-serializes a value into localStorage, ignoring quota/availability errors.
 *
 * @param key - The storage key.
 * @param value - The value to store.
 */
function writeJson(key: string, value: unknown) {
   try {
      localStorage.setItem(key, JSON.stringify(value))
   } catch {
      // Storage can be unavailable (private mode, quota); in-memory state still applies.
   }
}

/**
 * Loads stored entries, dropping anything that no longer validates against the
 * registry (e.g. a metric that was removed).
 *
 * @returns The valid stored entries.
 */
function loadStoredEntries(): MeasurementEntry[] {
   const parsed = readJson(MEASUREMENTS_STORAGE_KEY)
   if (!Array.isArray(parsed)) {
      return []
   }
   // Stored timestamps are trusted: skip the future check so clock changes never drop data.
   const farFuture = Number.MAX_SAFE_INTEGER
   return mergeMeasurementEntries(
      [],
      parsed.flatMap(item => {
         const checked = validateMeasurementEntry(item, { now: farFuture })
         return checked.ok ? [checked.entry] : []
      }),
   )
}

/**
 * Loads the ids of entries not yet pushed to the server.
 *
 * @returns The pending ids.
 */
function loadStoredPendingIds(): string[] {
   const parsed = readJson(PENDING_STORAGE_KEY)
   return Array.isArray(parsed) ? parsed.filter(id => typeof id === 'string') : []
}

let entryIdCounter = 0

/**
 * Generates a client entry id: a UUID where available, otherwise random hex from
 * `crypto.getRandomValues`, falling back to a time-plus-counter token.
 *
 * @returns The id.
 */
function createEntryId(): string {
   const cryptoApi = typeof crypto !== 'undefined' ? crypto : null
   if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
      return cryptoApi.randomUUID()
   }
   if (cryptoApi && typeof cryptoApi.getRandomValues === 'function') {
      const bytes = cryptoApi.getRandomValues(new Uint8Array(16))
      return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
   }
   entryIdCounter += 1
   return `m-${Date.now().toString(36)}-${entryIdCounter.toString(36).padStart(4, '0')}`
}

const [measurementEntries, setMeasurementEntries] =
   createSignal<MeasurementEntry[]>(loadStoredEntries())
const [pendingEntryIds, setPendingEntryIds] =
   createSignal<string[]>(loadStoredPendingIds())
const [measurementStatus, setMeasurementStatus] = createSignal<MeasurementStatus>({
   message: '',
   isError: false,
})

/** Entries that are not tombstones, newest first. */
const liveMeasurementEntries = createMemo(() =>
   measurementEntries().filter(entry => entry.deletedAt === null),
)

/**
 * Persists entries and pending ids, then notifies the legacy layer so a push is scheduled.
 *
 * @param entries - The next entry list.
 * @param pending - The next pending id list.
 */
function commit(entries: MeasurementEntry[], pending: string[]) {
   setMeasurementEntries(entries)
   setPendingEntryIds(pending)
   writeJson(MEASUREMENTS_STORAGE_KEY, entries)
   writeJson(PENDING_STORAGE_KEY, pending)
}

/**
 * Stores a locally changed entry and marks it for push.
 *
 * @param entry - The validated entry.
 */
function commitLocalChange(entry: MeasurementEntry) {
   const entries = mergeMeasurementEntries(measurementEntries(), [entry])
   const pending = [...new Set([...pendingEntryIds(), entry.entryId])]
   commit(entries, pending)
   dispatchMeasurementsStateChange()
}

/**
 * Returns the epoch-ms timestamp of an entry.
 *
 * @param entry - The entry.
 * @returns Epoch ms.
 */
export function getEntryTimestampMs(entry: MeasurementEntry) {
   return Date.parse(entry.timestamp)
}

/**
 * Returns the live entries for one metric, newest first.
 *
 * @param metricId - The metric id.
 * @returns The entries.
 */
export function getEntriesForMetric(metricId: MetricId) {
   return liveMeasurementEntries().filter(entry => entry.metricId === metricId)
}

/**
 * Adds a measurement, validating it against the registry.
 *
 * @param metricId - The metric id.
 * @param values - SI field values.
 * @param timestampMs - When the activity happened (defaults to now).
 * @returns The stored entry, or null with a status error when invalid.
 */
export function addMeasurement(
   metricId: MetricId,
   values: Record<string, number>,
   timestampMs = Date.now(),
): MeasurementEntry | null {
   const nowIso = new Date().toISOString()
   const checked = validateMeasurementEntry({
      entryId: createEntryId(),
      metricId,
      timestamp: new Date(timestampMs).toISOString(),
      values,
      deletedAt: null,
      updatedAt: nowIso,
   })
   if (!checked.ok) {
      setMeasurementStatus({
         message: translateValidationError(checked.error),
         isError: true,
      })
      return null
   }
   commitLocalChange(checked.entry)
   setMeasurementStatus({
      message: translate('Logged {metric}.', {
         metric: translate(getMetricDefinition(metricId)?.labelKey ?? metricId),
      }),
      isError: false,
   })
   return checked.entry
}

/**
 * Updates an entry's values and/or timestamp.
 *
 * @param entryId - The entry to update.
 * @param patch - New SI values and/or timestamp (epoch ms).
 * @returns Null on success, otherwise the validation error message.
 */
export function updateMeasurement(
   entryId: string,
   patch: { values?: Record<string, number>; timestampMs?: number },
): string | null {
   const existing = measurementEntries().find(entry => entry.entryId === entryId)
   if (!existing || existing.deletedAt !== null) {
      return translate('Selected entry is no longer available.')
   }
   const checked = validateMeasurementEntry({
      ...existing,
      values: patch.values ?? existing.values,
      timestamp:
         patch.timestampMs !== undefined ?
            new Date(patch.timestampMs).toISOString()
         :  existing.timestamp,
      updatedAt: new Date().toISOString(),
   })
   if (!checked.ok) {
      return translateValidationError(checked.error)
   }
   commitLocalChange(checked.entry)
   setMeasurementStatus({ message: translate('Entry updated.'), isError: false })
   return null
}

/**
 * Soft-deletes an entry so the tombstone propagates to other devices.
 *
 * @param entryId - The entry to delete.
 */
export function deleteMeasurement(entryId: string) {
   const existing = measurementEntries().find(entry => entry.entryId === entryId)
   if (!existing || existing.deletedAt !== null) {
      return
   }
   const nowIso = new Date().toISOString()
   commitLocalChange({ ...existing, values: {}, deletedAt: nowIso, updatedAt: nowIso })
   setMeasurementStatus({ message: translate('Entry deleted.'), isError: false })
}

/**
 * Merges entries received from the server. A pending local change stays pending
 * only while the local version still wins; otherwise the server superseded it.
 *
 * @param remote - Entries from the server.
 */
export function applyRemoteMeasurements(remote: readonly MeasurementEntry[]) {
   const merged = mergeMeasurementEntries(measurementEntries(), remote)
   const localById = new Map(measurementEntries().map(entry => [entry.entryId, entry]))
   const pending = pendingEntryIds().filter(id => {
      const local = localById.get(id)
      const now = merged.find(entry => entry.entryId === id)
      return Boolean(local && now && now.updatedAt === local.updatedAt)
   })
   commit(merged, pending)
}

/**
 * Returns the entries waiting to be pushed.
 *
 * @returns The pending entries.
 */
export function getPendingMeasurements(): MeasurementEntry[] {
   const ids = new Set(pendingEntryIds())
   return measurementEntries().filter(entry => ids.has(entry.entryId))
}

/**
 * Records the outcome of a push: applied and stale ids leave the pending set, and
 * the server's stored versions are merged in (a stale local edit is thereby replaced).
 *
 * @param results - Per-entry outcomes from the server.
 * @param serverEntries - The stored versions returned by the server.
 */
export function markMeasurementsPushed(
   results: readonly MeasurementUpsertOutcome[],
   serverEntries: readonly MeasurementEntry[],
) {
   const settled = new Set(
      results.flatMap(result => (result.entryId ? [result.entryId] : [])),
   )
   for (const result of results) {
      if (result.status === 'invalid') {
         console.warn('Server rejected measurement entry', result.entryId, result.error)
      }
   }
   const merged = mergeMeasurementEntries(measurementEntries(), serverEntries)
   commit(
      merged,
      pendingEntryIds().filter(id => !settled.has(id)),
   )
}

/**
 * Clears all measurements and pending pushes (account reset / key change).
 */
export function clearMeasurements() {
   commit([], [])
}

/**
 * Sets the status message shown under the entry form.
 *
 * @param message - The message (empty clears it).
 * @param isError - Whether it is an error.
 */
export function setMeasurementStatusMessage(message: string, isError = false) {
   setMeasurementStatus({ message, isError })
}

/**
 * Maps a registry validation error to a localized message.
 *
 * @param error - The validation error from the shared package.
 * @returns The localized message.
 */
function translateValidationError(error: string) {
   if (error.includes('future')) {
      return translate('Entries cannot be in the future.')
   }
   if (error.includes('positive')) {
      return translate('Enter values above 0.')
   }
   return translate('Enter a distance and a duration.')
}

export { liveMeasurementEntries, measurementEntries, measurementStatus }
